import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { client, useAuth, useDocumentTemplates, useI18n } from 'iomart-hooks';
import Iconify from 'src/components/iconify';
import { RHFAuto } from 'src/components/hook-form';
import { ROLES } from 'src/constants/roles';
import { CardBlock } from 'src/shorts/blocks/CardBlock';
import {
  createGenerateButtonConfig,
  deleteGenerateButtonConfig,
  deletePrivateFileById,
  updatePrivateFileComment,
  generatePrivateDocument,
  getPrivateFileBlobById,
  getPrivateImageDataUrl,
  listGenerateButtonConfigs,
  listPrivateFiles,
  uploadPrivateFile,
  type PrivateFileGenerateButtonConfig,
  type PrivateFileItem
} from 'src/utils/privateFiles';
import { SignPdfDrawer } from './SignPdfDrawer';
import { FormProvider as RHFormProvider, useForm, useFormContext } from 'react-hook-form';
import RHFJsonEditor from 'src/components/hook-form/RHFJsonEditor';
import RHFDate from 'src/components/hook-form/RHFDate';
import RHFTextField from 'src/components/hook-form/RHFTextField';
import RHFImageBase64Field from 'src/components/hook-form/RHFImageBase64Field';
import RHFMoney from 'src/components/hook-form/RHFMoney';
import { fCurrency } from 'src/utils/formatNumber';
import dayjs from 'dayjs';

type Props = {
  entityType: string;
  entityId?: number;
  editable?: boolean;
  title?: string;
  /** Hint text shown below the asset key input (e.g. "To upload barcode file use key \"barcode\"") */
  assetKeyHint?: string;
  /** Pre-defined asset keys shown as empty droppable areas; users can drag-and-drop or upload without typing the key */
  presetAssetKeys?: string[];
  allowedMimeTypes?: string[];
  /** When MIME is empty or octet-stream, validate by extension (e.g. ['.pdf', '.nlbl']) */
  allowedExtensions?: string[];
  onDeleteFile?: (fileKey: string) => void | Promise<void>;
  /** Called when a file is successfully uploaded (fileKey passed) */
  onFileUpload?: (fileKey: string) => void;
  onImagesChange?: (images: Record<string, string>) => void;
  generateDocuments?: GenerateDocumentConfig[];
  generateContext?: Record<string, any>;
};

type GenerateDocumentAdditionalValue = {
  name: string;
  label: string;
  required?: boolean;
  defaultValue?: string;
  placeholder?: string;
};

export type GenerateDocumentConfig = {
  assetCode: string;
  label: string;
  fileKey?: string;
  fileName?: string;
  values?: Record<string, any> | ((context?: Record<string, any>) => Record<string, any>);
  previewLoops?: Record<string, Record<string, any>[]>;
  additionalValues?: GenerateDocumentAdditionalValue[];
};

type ManageGenerateButtonsForm = {
  selectedTemplate: { id: number; name: string; assetCode?: string } | null;
  resultJson: string;
};

const extractTemplateVariables = (templateHtml: string) => {
  const html = String(templateHtml || '');
  const topLevel = new Set<string>();
  const loops: Record<string, Set<string>> = {};

  const loopRegex = /\{\$\$while-start\s+\$([a-zA-Z0-9_.]+)\}([\s\S]*?)\{\$\$while-end\}/g;
  let loopMatch: RegExpExecArray | null = loopRegex.exec(html);
  while (loopMatch) {
    const loopName = loopMatch[1];
    const content = loopMatch[2] || '';
    const fieldRegex = new RegExp(`\\{\\$${loopName}\\.([a-zA-Z0-9_.]+)(?:[^}]*)\\}`, 'g');
    loops[loopName] = loops[loopName] || new Set<string>();
    let fieldMatch: RegExpExecArray | null = fieldRegex.exec(content);
    while (fieldMatch) {
      loops[loopName].add(fieldMatch[1]);
      fieldMatch = fieldRegex.exec(content);
    }
    loopMatch = loopRegex.exec(html);
  }

  const varRegex = /\{\$([a-zA-Z0-9_.]+)(?:[^}]*)\}/g;
  let varMatch: RegExpExecArray | null = varRegex.exec(html);
  while (varMatch) {
    const varPath = varMatch[1];
    const [root, ...rest] = varPath.split('.');
    if (!root || rest.length === 0 || !loops[root]) {
      topLevel.add(varPath);
    }
    varMatch = varRegex.exec(html);
  }

  return {
    topLevelVariables: Array.from(topLevel).sort(),
    loopDefinitions: Object.entries(loops).map(([loopName, set]) => ({
      loopName,
      fields: Array.from(set).sort(),
    })),
  };
};

const createPromptFromTemplate = (params: {
  entityType: string;
  templateName: string;
  assetCode: string;
  entityDataShape: string;
  topLevelVariables: string[];
  loopDefinitions: Array<{ loopName: string; fields: string[] }>;
}) => {
  const { entityType, templateName, assetCode, entityDataShape, topLevelVariables, loopDefinitions } = params;
  const loopsDescription = loopDefinitions.length
    ? loopDefinitions
        .map((l) => `- ${l.loopName}: [${l.fields.join(', ')}]`)
        .join('\n')
    : '- no loops';

  return [
    'Generate config JSON for a document generation button.',
    '',
    `Entity type: ${entityType}`,
    `Template: ${templateName}`,
    `Template assetCode: ${assetCode}`,
    '',
    'Template variables (top-level):',
    topLevelVariables.length > 0 ? topLevelVariables.map((v) => `- ${v}`).join('\n') : '- no top-level variables',
    '',
    'Template loops and fields:',
    loopsDescription,
    '',
    'Entity data shape (keys and types only, values omitted):',
    entityDataShape,
    '',
    'Return ONLY JSON object with this shape:',
    '{',
    '  "label": "Human readable button title",',
    '  "assetCode": "template assetCode",',
    '  "values": { "varName": "mapped from entity parameters or imgPrivate:entityType:path.to.id:code" },',
    '  "previewLoops": { "items": [{ "field": "mapped from entity parameters" }] },',
    '  "additionalValues": [{ "name": "varName", "label": "Input label", "required": false }]',
    '}',
    '',
    'Rules:',
    '- map as many values as possible from entity parameters and field types',
    '- for images from supplier/buyer (stamp, logo) use imgPrivate:entityType:pathToEntityId:fileKey (e.g. imgPrivate:procurementSupplier:item.buyer.id:stamp)',
    '- include in additionalValues only variables that cannot be resolved from available parameters',
    '- keep keys exactly as template variables',
    '- supported format: fn:path[:param]. Examples: date:item.documentDate:DD.MM.YYYY, number:item.totalAmount:2, money:item.totalAmount:item.currency:2, imgPrivate:procurementSupplier:item.supplier.id:stamp, upper:item.supplier.name, default:item.tradeTerms:FOB',
    '- do not include fileKey or fileName in result JSON',
    '- do not wrap JSON in markdown',
  ].join('\n');
};

const buildEntityDataShape = (value: any, depth = 0): any => {
  if (depth > 5) return 'unknown';
  if (value === null || value === undefined) return 'unknown';
  if (Array.isArray(value)) {
    const first = value[0];
    if (first === undefined) return ['unknown'];
    return [buildEntityDataShape(first, depth + 1)];
  }
  const valueType = typeof value;
  if (valueType === 'string' || valueType === 'number' || valueType === 'boolean') {
    return valueType;
  }
  if (value instanceof Date) {
    return 'date';
  }
  if (valueType === 'object') {
    const result: Record<string, any> = {};
    Object.keys(value).forEach((key) => {
      result[key] = buildEntityDataShape(value[key], depth + 1);
    });
    return result;
  }
  return 'unknown';
};

type GenerateDrawerFormProps = {
  config: GenerateDocumentConfig;
  initialValues: Record<string, string>;
  generateContext?: Record<string, any>;
  isGenerating: boolean;
  onCancel: () => void;
  onSubmit: (values: Record<string, string>) => Promise<void>;
  onValidationError: (fieldLabel: string) => void;
};

const formatDateForPayload = (value: any): string => {
  if (!value) return '';
  if (dayjs.isDayjs(value)) return value.format('DD.MM.YYYY');
  return String(value);
};

/** Поле изображения с превью; для imgPrivate асинхронно загружает base64 из private storage. */
function ImageFieldWithPreview({
  name,
  label,
  valueExpr,
  generateContext,
}: {
  name: string;
  label: string;
  valueExpr: string;
  generateContext?: Record<string, any>;
}) {
  const { setValue, watch } = useFormContext<Record<string, any>>();
  const { t } = useI18n(false);
  const [loadError, setLoadError] = useState(false);
  const value = watch(name);
  const parsed = typeof valueExpr === 'string' ? parseImgPrivate(valueExpr) : null;
  const isImgPrivate = !!parsed;
  const isDataUrl = typeof value === 'string' && /^data:image\//i.test(value);

  useEffect(() => {
    if (!isImgPrivate || !parsed || !generateContext) return;
    setLoadError(false);
    setValue(name, '', { shouldDirty: false }); // clear imgPrivate string while loading
    const entityId = Number(getValueByPath(generateContext, parsed.pathToId));
    if (!entityId) {
      setValue(name, '', { shouldDirty: false });
      setLoadError(true);
      return;
    }
    let cancelled = false;
    getPrivateImageDataUrl(parsed.entityType, entityId, parsed.fileKey)
      .then((dataUrl) => {
        if (!cancelled && dataUrl) {
          setValue(name, dataUrl, { shouldDirty: false });
          setLoadError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setValue(name, '', { shouldDirty: false });
          setLoadError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isImgPrivate, parsed?.entityType, parsed?.pathToId, parsed?.fileKey, generateContext, name, setValue]);

  return (
    <Stack spacing={1}>
      <RHFImageBase64Field name={name} label={label} size="small" />
      {loadError && (
        <Typography variant="caption" color="error">
          {t('An error occurred while loading the image')}
        </Typography>
      )}
      {isDataUrl && !loadError && (
        <Box
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: 0.5,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 48,
            maxWidth: 140,
            bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
          }}
        >
          <Box
            component="img"
            src={value}
            alt=""
            sx={{ maxHeight: 72, maxWidth: 120, objectFit: 'contain' }}
          />
        </Box>
      )}
    </Stack>
  );
}

function GenerateDrawerForm({
  config,
  initialValues,
  generateContext,
  isGenerating,
  onCancel,
  onSubmit,
  onValidationError,
}: GenerateDrawerFormProps) {
  const { t } = useI18n(false);
  const valuesRaw =
    typeof config.values === 'function'
      ? (config.values as (ctx?: Record<string, any>) => Record<string, any>)(generateContext)
      : config.values || {};
  const methods = useForm<Record<string, any>>({
    defaultValues: initialValues,
  });

  useEffect(() => {
    if (config) methods.reset(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.assetCode]);

  const handleSubmit = methods.handleSubmit(async (data) => {
    if (!config) return;
    const requiredField = (config.additionalValues || []).find(
      (field) => field.required && !String(data[field.name] ?? '').trim()
    );
    if (requiredField) {
      onValidationError(requiredField.label);
      return;
    }
    const payload: Record<string, string> = {};
    Object.keys(data).forEach((key) => {
      const v = data[key];
      const fmt = getAdditionalFieldFormat(valuesRaw[key]);
      if (fmt === 'date') {
        payload[key] = formatDateForPayload(v);
      } else if (fmt === 'money') {
        const { currency, decimals } = getMoneyFieldParams(valuesRaw[key], generateContext);
        payload[key] = formatMoneyValue(v, currency, decimals);
      } else {
        payload[key] = String(v ?? '');
      }
    });
    await onSubmit(payload);
  });

  if (!config) return null;

  return (
    <Drawer
      anchor="right"
      open
      onClose={() => {
        if (isGenerating) return;
        onCancel();
      }}
      PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, p: 2 } }}
    >
      <RHFormProvider {...methods}>
        <Stack spacing={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {t('Generate document')}: {config.label}
            </Typography>
            <IconButton onClick={() => { if (!isGenerating) onCancel(); }}>
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t('Fill additional values before generation.')}
          </Typography>
          <Stack spacing={1.5}>
            {(config.additionalValues || []).map((field) => {
              const valueExpr = valuesRaw[field.name];
              const format = getAdditionalFieldFormat(valueExpr);
              if (format === 'date') {
                return (
                  <RHFDate
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    required={!!field.required}
                    placeholder={field.placeholder}
                    size="small"
                  />
                );
              }
              if (format === 'number') {
                return (
                  <RHFTextField
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    type="number"
                    required={!!field.required}
                    placeholder={field.placeholder}
                    size="small"
                  />
                );
              }
              if (format === 'money') {
                const { currency, decimals } = getMoneyFieldParams(valueExpr, generateContext);
                return (
                  <RHFMoney
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    currency={currency}
                    decimalScale={decimals}
                    required={!!field.required}
                    size="small"
                  />
                );
              }
              if (format === 'img') {
                return (
                  <ImageFieldWithPreview
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    valueExpr={String(valueExpr ?? '')}
                    generateContext={generateContext}
                  />
                );
              }
              return (
                <RHFTextField
                  key={field.name}
                  name={field.name}
                  label={field.label}
                  required={!!field.required}
                  placeholder={field.placeholder}
                  size="small"
                />
              );
            })}
          </Stack>
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button color="inherit" onClick={onCancel} disabled={isGenerating}>
              {t('Cancel')}
            </Button>
            <Button variant="contained" disabled={isGenerating} onClick={handleSubmit}>
              {t('Create')}
            </Button>
          </Box>
        </Stack>
      </RHFormProvider>
    </Drawer>
  );
}

type DeleteTarget = {
  id: number;
  fileKey: string;
  originalName: string;
  version?: number;
  source: 'asset-key' | 'history';
};

const normalizeAssetKeyInput = (value: string) =>
  value
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-');

const getFileExtLabel = (fileName: string) => {
  const ext = String(fileName || '').split('.').pop() || 'file';
  return ext.slice(0, 8).toUpperCase();
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

type UploadValidateParams = {
  file: File;
  fileKey: string;
  groupedByKey: Record<string, PrivateFileItem[]>;
  allowedMimeSet: Set<string> | null;
  allowedExtSet: Set<string> | null;
  allowedExtensions?: string[];
};

const validateFileForUpload = (
  params: UploadValidateParams
): { valid: boolean; message?: string } => {
  const {
    file,
    fileKey,
    groupedByKey,
    allowedMimeSet,
    allowedExtSet,
    allowedExtensions = [],
  } = params;
  const mimeOk = !allowedMimeSet || allowedMimeSet.has(file.type);
  const mimeUnknown = !file.type || file.type === 'application/octet-stream';
  const extOk = !allowedExtSet || allowedExtSet.has(getFileExtension(file.name));
  const allowedByExt = mimeUnknown && allowedExtSet && extOk;
  if (!mimeOk && !allowedByExt) {
    return { valid: false, message: 'This file type is not allowed for this entity.' };
  }
  if (mimeUnknown && allowedExtSet && !extOk) {
    return {
      valid: false,
      message: `Allowed file extensions: ${allowedExtensions.join(', ')}`,
    };
  }
  const existingByKey = groupedByKey[fileKey] || [];
  const latestByKey = existingByKey[0];
  if (latestByKey?.mimeType) {
    const latestMime = String(latestByKey.mimeType);
    const latestIsImage = latestMime.startsWith('image/');
    const incomingIsImage = String(file.type || '').startsWith('image/');
    if (latestIsImage && !incomingIsImage) {
      return {
        valid: false,
        message: 'This asset key is already image-based. Upload an image for the same key.',
      };
    }
    if (!latestIsImage && latestMime !== file.type) {
      return {
        valid: false,
        message: `This asset key is locked to ${latestMime}. Upload the same file type.`,
      };
    }
  }
  return { valid: true };
};

const getFileExtension = (fileName: string) => {
  const name = String(fileName || '');
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
};

const getValueByPath = (obj: any, path: string) => {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
};

const looksLikePath = (value: string) => /^[a-zA-Z_][a-zA-Z0-9_.]*$/.test(String(value || '').trim());

const resolvePathValue = (path: string, context: Record<string, any>, row?: Record<string, any>) => {
  if (!path) return undefined;
  if (row) {
    const fromRow = getValueByPath(row, path);
    if (fromRow !== undefined) return fromRow;
    const fromRowDirect = row[path];
    if (fromRowDirect !== undefined) return fromRowDirect;
  }
  const fromContext = getValueByPath(context, path);
  if (fromContext !== undefined) return fromContext;
  return undefined;
};

const pad2 = (value: number) => String(value).padStart(2, '0');

const formatDateByPattern = (input: any, format = 'DD.MM.YYYY') => {
  if (input === null || input === undefined || input === '') return '';
  const date = input instanceof Date ? input : new Date(String(input));
  if (Number.isNaN(date.getTime())) return String(input);
  const map: Record<string, string> = {
    YYYY: String(date.getFullYear()),
    MM: pad2(date.getMonth() + 1),
    DD: pad2(date.getDate()),
    HH: pad2(date.getHours()),
    mm: pad2(date.getMinutes()),
    ss: pad2(date.getSeconds()),
  };
  return String(format || 'DD.MM.YYYY').replace(/YYYY|MM|DD|HH|mm|ss/g, (token) => map[token] || token);
};

const CURRENCY_CODES = ['RUB', 'USD', 'EUR', 'CNY'] as const;
type CurrencyCode = (typeof CURRENCY_CODES)[number];

const formatMoneyValue = (value: any, currency: string, decimals: number): string => {
  const num = Number(value);
  if (Number.isNaN(num)) return String(value ?? '');
  const code = (CURRENCY_CODES.includes(currency as CurrencyCode) ? currency : 'RUB') as CurrencyCode;
  return fCurrency(num, code, decimals);
};

const applyFormatFunction = (
  fnName: string,
  value: any,
  args: string[],
  context?: Record<string, any>,
  row?: Record<string, any>
) => {
  const fn = String(fnName || '').toLowerCase();
  if (fn === 'date') {
    return formatDateByPattern(value, args[0] || 'DD.MM.YYYY');
  }
  if (fn === 'number') {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return value;
    const decimals = Math.max(0, Number(args[0] || 0));
    return numeric.toFixed(decimals);
  }
  if (fn === 'upper') {
    return String(value ?? '').toUpperCase();
  }
  if (fn === 'lower') {
    return String(value ?? '').toLowerCase();
  }
  if (fn === 'trim') {
    return String(value ?? '').trim();
  }
  if (fn === 'default') {
    const fallback = args.join(':');
    if (value === undefined || value === null || String(value).trim() === '') return fallback;
    return value;
  }
  const moneyMatch = fnName.match(/^money(rub|usd|eur|cny)?$/i);
  if (moneyMatch) {
    const decimals = Math.max(0, Number(args[args.length - 1] || 2));
    let currency: string;
    if (moneyMatch[1]) {
      currency = moneyMatch[1].toUpperCase();
    } else {
      const pathToCode = args[0];
      currency = pathToCode && context
        ? String(resolvePathValue(pathToCode, context, row) ?? 'RUB').toUpperCase()
        : 'RUB';
    }
    return formatMoneyValue(value, currency, decimals);
  }
  return value;
};

/** imgPrivate:entityType:pathToEntityId:fileKey — загружает изображение из private storage (например stamp/logo поставщика). pathToEntityId — путь в context, например item.buyer.id. */
const IMG_PRIVATE_PREFIX = 'imgprivate:';
const parseImgPrivate = (value: string): { entityType: string; pathToId: string; fileKey: string } | null => {
  const trimmed = value.trim();
  const lower = trimmed.toLowerCase();
  if (!lower.startsWith(IMG_PRIVATE_PREFIX)) return null;
  const rest = trimmed.slice(IMG_PRIVATE_PREFIX.length);
  const parts = rest.split(':');
  if (parts.length < 3) return null;
  const [entityType, pathToId, fileKey] = [parts[0], parts[1], parts[2]];
  return entityType && pathToId && fileKey ? { entityType, pathToId, fileKey } : null;
};

const resolveMaybePathValue = (raw: any, context: Record<string, any>, row?: Record<string, any>) => {
  if (typeof raw !== 'string') return raw;
  const value = raw.trim();
  if (!value) return value;

  // Function format: fn:path[:arg1[:arg2...]]
  const tokens = value.split(':');
  if (tokens.length >= 2) {
    const fnName = (tokens[0] || '').toLowerCase();
    const sourceExpr = tokens[1];
    const args = tokens.slice(2);
    if (fnName === 'img') {
      return looksLikePath(sourceExpr)
        ? resolvePathValue(sourceExpr, context, row)
        : sourceExpr;
    }
    if (fnName === 'imgprivate') {
      const parsed = parseImgPrivate(value);
      if (parsed) return value;
      return raw;
    }
    const moneyMatch = fnName.match(/^money(rub|usd|eur|cny)?$/i);
    if (moneyMatch || fnName === 'money') {
      const sourceValue = looksLikePath(sourceExpr)
        ? resolvePathValue(sourceExpr, context, row)
        : sourceExpr;
      return applyFormatFunction(fnName, sourceValue, args, context, row);
    }
    if (/^(date|number|upper|lower|trim|default)$/i.test(fnName)) {
      const sourceValue = looksLikePath(sourceExpr)
        ? resolvePathValue(sourceExpr, context, row)
        : sourceExpr;
      return applyFormatFunction(fnName, sourceValue, args);
    }
  }

  if (!looksLikePath(value)) return raw;
  const resolved = resolvePathValue(value, context, row);
  if (resolved !== undefined) return resolved;
  return raw;
};

type MoneyFieldParams = { currency: CurrencyCode; decimals: number };

const getAdditionalFieldFormat = (
  valueExpr: string | undefined
): 'date' | 'number' | 'img' | 'money' | 'text' => {
  if (!valueExpr || typeof valueExpr !== 'string') return 'text';
  const tokens = valueExpr.trim().split(':');
  if (tokens.length >= 2) {
    const fn = (tokens[0] || '').toLowerCase();
    if (fn === 'date') return 'date';
    if (fn === 'number') return 'number';
    if (fn === 'img' || fn === 'imgprivate') return 'img';
    if (fn === 'money' || /^money(rub|usd|eur|cny)$/i.test(fn)) return 'money';
  }
  return 'text';
};

const getMoneyFieldParams = (
  valueExpr: string | undefined,
  context?: Record<string, any>
): MoneyFieldParams => {
  const decimals = 2;
  const currency: CurrencyCode = 'RUB';
  if (!valueExpr || typeof valueExpr !== 'string') return { currency, decimals };
  const tokens = valueExpr.trim().split(':');
  if (tokens.length >= 2) {
    const fn = (tokens[0] || '').toLowerCase();
    const match = fn.match(/^money(rub|usd|eur|cny)$/i);
    if (match) {
      const dec = Math.max(0, Number(tokens[tokens.length - 1] || 2));
      return { currency: match[1].toUpperCase() as CurrencyCode, decimals: dec };
    }
    if (fn === 'money' && tokens.length >= 4) {
      const pathToCode = tokens[2];
      const dec = Math.max(0, Number(tokens[3] || 2));
      const resolved =
        pathToCode && context && looksLikePath(pathToCode)
          ? resolvePathValue(pathToCode, context)
          : 'RUB';
      const code = String(resolved ?? 'RUB').toUpperCase();
      return {
        currency: CURRENCY_CODES.includes(code as CurrencyCode) ? (code as CurrencyCode) : 'RUB',
        decimals: dec,
      };
    }
    if (fn === 'money' && tokens.length >= 3) {
      const dec = Math.max(0, Number(tokens[2] || 2));
      return { currency: 'RUB', decimals: dec };
    }
  }
  return { currency, decimals };
};

const resolveValuesMap = (values: Record<string, any>, context: Record<string, any>) => {
  const result: Record<string, any> = {};
  Object.keys(values || {}).forEach((key) => {
    result[key] = resolveMaybePathValue(values[key], context);
  });
  return result;
};

/** Разрешает values асинхронно; для imgPrivate:entityType:pathToId:fileKey подставляет data URL из private storage. */
const resolveValuesMapAsync = async (
  values: Record<string, any>,
  context: Record<string, any>
): Promise<Record<string, any>> => {
  const result: Record<string, any> = {};
  const keys = Object.keys(values || {});
  for (const key of keys) {
    const raw = values[key];
    const imgPrivateParsed = typeof raw === 'string' && parseImgPrivate(raw);
    if (imgPrivateParsed) {
      const entityId = Number(getValueByPath(context, imgPrivateParsed.pathToId));
      if (entityId && imgPrivateParsed.entityType && imgPrivateParsed.fileKey) {
        try {
          const dataUrl = await getPrivateImageDataUrl(
            imgPrivateParsed.entityType,
            entityId,
            imgPrivateParsed.fileKey
          );
          result[key] = dataUrl ?? '';
        } catch {
          result[key] = '';
        }
      } else {
        result[key] = '';
      }
    } else {
      result[key] = resolveMaybePathValue(raw, context);
    }
  }
  return result;
};

const toLiteralValuesMap = (values: Record<string, any>) => {
  const result: Record<string, any> = {};
  Object.keys(values || {}).forEach((key) => {
    result[key] = values[key];
  });
  return result;
};

const resolvePreviewLoopsMap = (
  previewLoops: Record<string, Record<string, any>[]>,
  context: Record<string, any>,
  resolvedValues: Record<string, any>
) => {
  const result: Record<string, Record<string, any>[]> = {};
  Object.keys(previewLoops || {}).forEach((loopName) => {
    const templateRows = Array.isArray(previewLoops[loopName]) ? previewLoops[loopName] : [];
    if (templateRows.length === 0) {
      result[loopName] = [];
      return;
    }

    const descriptor = templateRows[0] || {};
    const sourceRowsRaw =
      (Array.isArray(resolvedValues?.[loopName]) && resolvedValues[loopName]) ||
      (Array.isArray(getValueByPath(context, loopName)) && getValueByPath(context, loopName)) ||
      (Array.isArray(getValueByPath(context, `item.${loopName}`)) && getValueByPath(context, `item.${loopName}`)) ||
      [];

    if (!Array.isArray(sourceRowsRaw) || sourceRowsRaw.length === 0) {
      result[loopName] = templateRows.map((row) => {
        const nextRow: Record<string, any> = {};
        Object.keys(row || {}).forEach((fieldKey) => {
          nextRow[fieldKey] = resolveMaybePathValue(row[fieldKey], context);
        });
        return nextRow;
      });
      return;
    }

    result[loopName] = sourceRowsRaw.map((sourceRow: any) => {
      const nextRow: Record<string, any> = {};
      Object.keys(descriptor).forEach((fieldKey) => {
        nextRow[fieldKey] = resolveMaybePathValue(descriptor[fieldKey], context, sourceRow);
      });
      return nextRow;
    });
  });
  return result;
};

export default function PrivateFilesBlock({
  entityType,
  entityId,
  editable = false,
  title = 'Template assets (private)',
  assetKeyHint,
  presetAssetKeys,
  allowedMimeTypes,
  allowedExtensions,
  onDeleteFile,
  onFileUpload,
  onImagesChange,
  generateDocuments,
  generateContext,
}: Props) {
  const { t } = useI18n(false);
  const { role } = (useAuth as any)(false) ?? {};
  const isAdmin = role === ROLES.ADMIN;
  const { enqueueSnackbar } = useSnackbar();
  const manageFormMethods = useForm<ManageGenerateButtonsForm>({
    defaultValues: {
      selectedTemplate: null,
      resultJson: '',
    },
  });
  const [assetKey, setAssetKey] = useState('');
  const [privateAssets, setPrivateAssets] = useState<PrivateFileItem[]>([]);
  const [privateAssetImages, setPrivateAssetImages] = useState<Record<string, string>>({});
  const [historyKey, setHistoryKey] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [generateMenuAnchor, setGenerateMenuAnchor] = useState<null | HTMLElement>(null);
  const [generateConfirmTarget, setGenerateConfirmTarget] = useState<GenerateDocumentConfig | null>(null);
  const [generateDrawerTarget, setGenerateDrawerTarget] = useState<GenerateDocumentConfig | null>(null);
  const [generateAdditionalValues, setGenerateAdditionalValues] = useState<Record<string, string>>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [storedGenerateDocuments, setStoredGenerateDocuments] = useState<PrivateFileGenerateButtonConfig[]>([]);
  const [isManageDrawerOpen, setIsManageDrawerOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<{ id: number; name: string; assetCode?: string } | null>(null);
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [isDeletingConfigId, setIsDeletingConfigId] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const [dragOverAssetKey, setDragOverAssetKey] = useState(false);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<{ key: string; action: 'open' | 'download' } | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingCommentValue, setEditingCommentValue] = useState('');
  const [signDrawerTarget, setSignDrawerTarget] = useState<PrivateFileItem | null>(null);
  const targetId = Number(entityId || 0);
  const allowedMimeSet = allowedMimeTypes?.length ? new Set(allowedMimeTypes) : null;
  const allowedExtSet =
    allowedExtensions?.length
      ? new Set(allowedExtensions.map((e) => (e.startsWith('.') ? e : `.${e}`).toLowerCase()))
      : null;
  const propGenerateDocuments = (generateDocuments || []).filter((item) => !!item?.assetCode && !!item?.label);
  const storedGenerateConfigs = storedGenerateDocuments.map((item) => ({
    assetCode: item.assetCode,
    label: item.label,
    fileKey: item.fileKey,
    fileName: item.fileName,
    values: item.values || {},
    previewLoops: item.previewLoops || {},
    additionalValues: item.additionalValues || [],
  })) as GenerateDocumentConfig[];
  const availableGenerateDocuments = [...propGenerateDocuments, ...storedGenerateConfigs];

  const refreshPrivateAssets = async () => {
    if (!targetId) {
      setPrivateAssets([]);
      setPrivateAssetImages({});
      onImagesChange?.({});
      return [] as PrivateFileItem[];
    }
    const assets = await listPrivateFiles(entityType, targetId);
    setPrivateAssets(assets);

    const imageAssets = assets.filter((asset) => String(asset.mimeType || '').startsWith('image/'));
    const imageEntries = await Promise.all(
      imageAssets.map(async (asset) => {
        try {
          const dataUrl = await getPrivateImageDataUrl(entityType, targetId, asset.fileKey);
          return [asset.fileKey, dataUrl] as const;
        } catch {
          return [asset.fileKey, ''] as const;
        }
      })
    );
    const nextImages = imageEntries.reduce((acc, [key, dataUrl]) => {
      if (dataUrl) acc[key] = dataUrl;
      return acc;
    }, {} as Record<string, string>);
    setPrivateAssetImages(nextImages);
    onImagesChange?.(nextImages);
    return assets;
  };

  const refreshGenerateConfigs = async () => {
    if (!isAdmin) {
      setStoredGenerateDocuments([]);
      return;
    }
    try {
      const items = await listGenerateButtonConfigs(entityType);
      setStoredGenerateDocuments(items);
    } catch (err: any) {
      enqueueSnackbar(err?.message || t('Failed to load generate button configs'), { variant: 'error' });
    }
  };

  const groupedByKey = privateAssets.reduce((acc, file) => {
    const key = file.fileKey;
    if (!acc[key]) acc[key] = [];
    acc[key].push(file);
    return acc;
  }, {} as Record<string, PrivateFileItem[]>);

  Object.keys(groupedByKey).forEach((key) => {
    groupedByKey[key].sort((a, b) => {
      const versionDiff = Number(b.version || 0) - Number(a.version || 0);
      if (versionDiff !== 0) return versionDiff;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  });

  const latestAssets = Object.values(groupedByKey).map((versions) => versions[0]);
  const existingKeys = new Set(Object.keys(groupedByKey));
  const presetKeysWithoutFiles = (presetAssetKeys || [])
    .map((k) => normalizeAssetKeyInput(String(k || '')))
    .filter((k) => k && !existingKeys.has(k));
  const historyFiles = historyKey ? (groupedByKey[historyKey] || []) : [];

  const handleUploadToKey = async (file: File, fileKey: string) => {
    if (!targetId || !fileKey.trim()) return;
    const key = fileKey.trim();
    const validation = validateFileForUpload({
      file,
      fileKey: key,
      groupedByKey,
      allowedMimeSet,
      allowedExtSet,
      allowedExtensions,
    });
    if (!validation.valid) {
      enqueueSnackbar(t(validation.message!), { variant: 'error' });
      return;
    }
    setUploadingKey(key);
    try {
      await uploadPrivateFile(entityType, targetId, key, file);
      enqueueSnackbar(t('Asset uploaded successfully'), { variant: 'success' });
      await refreshPrivateAssets();
      onFileUpload?.(key);
    } catch (err: any) {
      enqueueSnackbar(err?.message || t('Failed to upload asset'), { variant: 'error' });
    } finally {
      setUploadingKey(null);
    }
  };

  const handleDropOnBlock = (fileKey: string) => (event: React.DragEvent) => {
    event.preventDefault();
    setDragOverKey(null);
    const file = event.dataTransfer?.files?.[0];
    if (!file || !editable || !targetId) return;
    handleUploadToKey(file, fileKey);
  };

  const handleDropOnAssetKeyArea = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOverAssetKey(false);
    const file = event.dataTransfer?.files?.[0];
    if (!file || !editable || !targetId) return;
    const key = assetKey.trim();
    if (!key) {
      enqueueSnackbar(t('Enter asset key first, then drop the file'), { variant: 'error' });
      return;
    }
    handleUploadToKey(file, key);
  };

  const getFileBadgeStyle = (fileName: string) => {
    const ext = String(fileName || '').split('.').pop()?.toLowerCase() || '';
    if (ext === 'pdf') {
      return { bg: '#d92d20', color: '#ffffff', fontSize: 18 };
    }
    if (ext === 'zip' || ext === 'rar' || ext === '7z') {
      return { bg: '#344054', color: '#ffffff', fontSize: 16 };
    }
    if (ext === 'doc' || ext === 'docx') {
      return { bg: '#175cd3', color: '#ffffff', fontSize: 16 };
    }
    if (ext === 'xls' || ext === 'xlsx') {
      return { bg: '#067647', color: '#ffffff', fontSize: 16 };
    }
    return { bg: '#475467', color: '#ffffff', fontSize: 14 };
  };

  const resolveGenerateValues = (config: GenerateDocumentConfig) => {
    if (typeof config.values === 'function') {
      try {
        return config.values(generateContext) || {};
      } catch {
        return {};
      }
    }
    return config.values || {};
  };

  /** Имя файла по умолчанию для сохранения: из context (например item.documentNumber) + assetCode/label, чтобы не получать "_.pdf". */
  const getDefaultGeneratedFileName = (config: GenerateDocumentConfig, context: Record<string, any>) => {
    const safe = (s: string) => String(s).replace(/[^\w\-]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 80) || 'document';
    const part1 = context?.item?.documentNumber ?? context?.item?.name ?? context?.item?.id ?? 'document';
    const part2 = config.assetCode || config.label || 'file';
    return `${safe(String(part1))}_${safe(String(part2))}.pdf`;
  };

  const runGenerateDocument = async (config: GenerateDocumentConfig, additionalValues: Record<string, string>) => {
    if (!targetId) return;
    try {
      setIsGenerating(true);
      const context = (generateContext || {}) as Record<string, any>;
      const baseValuesRaw = resolveGenerateValues(config);
      const baseValuesResolved = await resolveValuesMapAsync(baseValuesRaw, context);
      const additionalValuesResolved = toLiteralValuesMap(additionalValues || {});
      const merged = { ...baseValuesResolved, ...additionalValuesResolved };
      // Resolve imgPrivate in merged (e.g. user kept imgPrivate string in form) to base64 before sending
      const finalValues = await resolveValuesMapAsync(merged, context);
      const finalPreviewLoops = resolvePreviewLoopsMap(
        (config.previewLoops || {}) as Record<string, Record<string, any>[]>,
        context,
        finalValues
      );
      const fileName = config.fileName?.trim() || getDefaultGeneratedFileName(config, context);
      await generatePrivateDocument({
        assetCode: config.assetCode,
        entityType,
        entityId: targetId,
        fileName,
        fileKey: config.fileKey || undefined,
        previewValues: finalValues,
        additionalValues: {},
        previewLoops: finalPreviewLoops,
      });
      await refreshPrivateAssets();
      enqueueSnackbar(t('Document generated successfully'), { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.message || t('Failed to generate document'), { variant: 'error' });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleting(true);
      await deletePrivateFileById(entityType, targetId, deleteTarget.id);
      const assetsAfterDelete = await refreshPrivateAssets();
      const remainsForKey = assetsAfterDelete.filter((x) => x.fileKey === deleteTarget.fileKey).length;
      if (onDeleteFile && remainsForKey === 0) {
        await onDeleteFile(deleteTarget.fileKey);
      }
      enqueueSnackbar(t('Asset deleted'), { variant: 'success' });
      setDeleteTarget(null);
    } catch (err: any) {
      enqueueSnackbar(err?.message || t('Failed to delete asset'), { variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const getFormDefaultForField = (fieldName: string, valueExpr: string, context: Record<string, any>) => {
    const format = getAdditionalFieldFormat(valueExpr);
    if (format === 'date') {
      const tokens = String(valueExpr || '').trim().split(':');
      const path = tokens[1];
      if (path && looksLikePath(path)) {
        const raw = resolvePathValue(path, context);
        if (raw instanceof Date) return raw.toISOString().slice(0, 10);
        if (raw && typeof raw === 'string' && /^\d{2}\.\d{2}\.\d{4}/.test(raw)) {
          const [d, m, y] = raw.split('.');
          return `${y}-${m}-${d}`;
        }
        if (raw) return String(raw).slice(0, 10);
      }
    }
    if (format === 'money') {
      const tokens = String(valueExpr || '').trim().split(':');
      const path = tokens[1];
      if (path && looksLikePath(path)) {
        const raw = resolvePathValue(path, context);
        const num = Number(raw);
        if (!Number.isNaN(num)) return String(num);
      }
    }
    const resolved = resolveMaybePathValue(valueExpr, context);
    if (resolved !== undefined && resolved !== null) return String(resolved);
    return '';
  };

  const handleGenerateClick = (config: GenerateDocumentConfig) => {
    const additional = config.additionalValues || [];
    if (additional.length > 0) {
      const context = (generateContext || {}) as Record<string, any>;
      const baseValuesRaw = resolveGenerateValues(config);
      const defaults = additional.reduce((acc, field) => {
        const valueExpr = baseValuesRaw[field.name];
        if (valueExpr !== undefined) {
          const val = getFormDefaultForField(field.name, valueExpr, context);
          acc[field.name] = val;
        } else {
          acc[field.name] = field.defaultValue || '';
        }
        return acc;
      }, {} as Record<string, string>);
      setGenerateAdditionalValues(defaults);
      setGenerateDrawerTarget(config);
      return;
    }
    setGenerateConfirmTarget(config);
  };

  const handleTemplateChooseForPrompt = async (
    template: { id: number; name: string; assetCode?: string } | null
  ) => {
    setSelectedTemplate(template);
    if (!template?.id) {
      setGeneratedPrompt('');
      return;
    }
    try {
      const response = await client.query({
        oneDocumentTemplate: {
          id: true,
          name: true,
          assetCode: true,
          templateHtml: true,
          __args: { id: template.id },
        },
      });
      const oneTemplate = response?.oneDocumentTemplate;
      const templateHtml = String(oneTemplate?.templateHtml || '');
      const { topLevelVariables, loopDefinitions } = extractTemplateVariables(templateHtml);
      const entityShape = buildEntityDataShape(generateContext || {});
      const entityDataShape = JSON.stringify(entityShape, null, 2);
      const prompt = createPromptFromTemplate({
        entityType,
        templateName: String(oneTemplate?.name || template.name || ''),
        assetCode: String(oneTemplate?.assetCode || template.assetCode || ''),
        entityDataShape,
        topLevelVariables,
        loopDefinitions,
      });
      setGeneratedPrompt(prompt);
    } catch (err: any) {
      enqueueSnackbar(err?.message || t('Failed to build prompt from template'), { variant: 'error' });
    }
  };

  const handleCreateGenerateConfig = async () => {
    try {
      setIsSavingConfig(true);
      const jsonResultInput = String(manageFormMethods.getValues('resultJson') || '');
      const parsed = JSON.parse(jsonResultInput || '{}');
      const payload = {
        label: String(parsed?.label || '').trim(),
        assetCode: String(parsed?.assetCode || selectedTemplate?.assetCode || '').trim(),
        values: parsed?.values && typeof parsed.values === 'object' ? parsed.values : {},
        previewLoops:
          parsed?.previewLoops && typeof parsed.previewLoops === 'object' ? parsed.previewLoops : {},
        additionalValues: Array.isArray(parsed?.additionalValues) ? parsed.additionalValues : [],
      };
      if (!payload.label || !payload.assetCode) {
        throw new Error(t('Label and asset code are required in JSON'));
      }
      await createGenerateButtonConfig(entityType, payload);
      await refreshGenerateConfigs();
      manageFormMethods.setValue('resultJson', '');
      enqueueSnackbar(t('Generate button saved'), { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.message || t('Failed to create generate button'), { variant: 'error' });
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleDeleteGenerateConfig = async (id: string) => {
    try {
      setIsDeletingConfigId(id);
      await deleteGenerateButtonConfig(entityType, id);
      await refreshGenerateConfigs();
      enqueueSnackbar(t('Generate button deleted'), { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.message || t('Failed to delete generate button'), { variant: 'error' });
    } finally {
      setIsDeletingConfigId(null);
    }
  };

  const handleEditGenerateConfig = (item: PrivateFileGenerateButtonConfig) => {
    const editablePayload = {
      label: item.label,
      assetCode: item.assetCode,
      values: item.values || {},
      previewLoops: item.previewLoops || {},
      additionalValues: item.additionalValues || [],
    };
    manageFormMethods.setValue('resultJson', JSON.stringify(editablePayload, null, 2));
  };

  useEffect(() => {
    refreshPrivateAssets().catch(() => {
      enqueueSnackbar(t('Failed to load private assets'), { variant: 'error' });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, targetId]);

  useEffect(() => {
    if (!isAdmin) {
      setStoredGenerateDocuments([]);
      return;
    }
    refreshGenerateConfigs().catch(() => {
      enqueueSnackbar(t('Failed to load generate button configs'), { variant: 'error' });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, isAdmin]);

  const cardTitleNode = (
    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
        {t(title)}
      </Typography>
      {isAdmin && generateContext ? (
        <Tooltip title={t('Manage generator buttons')}>
          <IconButton
            size="small"
            onClick={() => setIsManageDrawerOpen(true)}
            sx={{ p: 0.5 }}
          >
            <Iconify icon="eva:settings-2-outline" width={16} />
          </IconButton>
        </Tooltip>
      ) : (
        <Box sx={{ width: 24, height: 24 }} />
      )}
    </Box>
  );

  return (
    <CardBlock title={cardTitleNode}>
      <Stack spacing={2}>
        <Typography variant="caption" color="text.secondary">
          {t('Files are stored in private storage and are not accessible by public direct links.')}
        </Typography>

        {availableGenerateDocuments.length > 0 && (
          <Stack spacing={1}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              {availableGenerateDocuments.length === 1 ? (
                <Button
                  variant="contained"
                  startIcon={<Iconify icon="eva:file-add-fill" />}
                  disabled={!targetId || isGenerating}
                  onClick={() => handleGenerateClick(availableGenerateDocuments[0])}
                  sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                >
                  {availableGenerateDocuments[0].label}
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    endIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}
                    disabled={!targetId || isGenerating}
                    onClick={(event) => setGenerateMenuAnchor(event.currentTarget)}
                    sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                  >
                    {t('Generate document')}
                  </Button>
                  <Menu
                    open={!!generateMenuAnchor}
                    anchorEl={generateMenuAnchor}
                    onClose={() => setGenerateMenuAnchor(null)}
                  >
                    {availableGenerateDocuments.map((docItem) => (
                      <MenuItem
                        key={`${docItem.assetCode}-${docItem.label}`}
                        onClick={() => {
                          setGenerateMenuAnchor(null);
                          handleGenerateClick(docItem);
                        }}
                      >
                        {docItem.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </Stack>
            {!targetId && (
              <Typography variant="caption" color="text.secondary">
                {t('Save item first to generate documents.')}
              </Typography>
            )}
          </Stack>
        )}

        {editable ? (
          <>
            {!targetId && (
              <Alert severity="info">{t('Save item first to upload private files.')}</Alert>
            )}
            <Stack spacing={1}>
              <Box
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (editable && targetId) setDragOverAssetKey(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverAssetKey(false);
                }}
                onDrop={handleDropOnAssetKeyArea}
                sx={{
                  position: 'relative',
                  p: 1.5,
                  border: (theme) =>
                    `1px dashed ${dragOverAssetKey ? theme.palette.primary.main : theme.palette.divider}`,
                  borderRadius: 1,
                  bgcolor: dragOverAssetKey ? 'action.hover' : undefined,
                  transition: 'border-color 0.2s, background-color 0.2s',
                }}
              >
                {(uploadingKey && uploadingKey === assetKey.trim()) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(255,255,255,0.8)',
                      borderRadius: 1,
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress size={32} />
                  </Box>
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField
                    size="small"
                    fullWidth
                    label={t('Asset key')}
                    value={assetKey}
                    onChange={(event) => setAssetKey(normalizeAssetKeyInput(event.target.value))}
                    sx={{
                      '& .MuiInputBase-input': { fontSize: '13px' },
                      '& .MuiInputLabel-root': { fontSize: '13px' },
                    }}
                  />
                  <Button
                component="label"
                variant="outlined"
                disabled={!targetId || !assetKey.trim() || !!(uploadingKey && uploadingKey === assetKey.trim())}
                startIcon={uploadingKey === assetKey.trim() ? <CircularProgress size={16} /> : <Iconify icon="eva:cloud-upload-fill" />}
                sx={{ alignSelf: { xs: 'stretch', sm: 'end' } }}
              >
                {t('Upload')}
                <input
                  hidden
                  type="file"
                  accept={allowedExtensions?.length ? allowedExtensions.join(',') : allowedMimeTypes?.join(',')}
                  onChange={async (event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (!file || !targetId || !assetKey.trim()) return;
                    await handleUploadToKey(file, assetKey.trim());
                  }}
                />
              </Button>
                </Stack>
                {dragOverAssetKey && (
                  <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                    {assetKey.trim() ? t('Drop file to upload') : t('Enter asset key first, then drop file')}
                  </Typography>
                )}
              </Box>
            {assetKeyHint && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5 }}>
                {assetKeyHint}
              </Typography>
            )}
          </Stack>
          </>
        ) : (
          <Alert severity="info">
            {t('Upload is available only in edit mode after saving the item.')}
          </Alert>
        )}

        <Box
          display="grid"
          gridTemplateColumns={{ xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }}
          gap={1.5}
        >
          {latestAssets.length > 0 ? (
            latestAssets.map((asset) => (
              (() => {
                const isImageAsset = String(asset.mimeType || '').startsWith('image/');
                const isPdfAsset =
                  String(asset.mimeType || '').toLowerCase().includes('application/pdf') ||
                  /\.pdf$/i.test(asset.originalName || '');
                const isDragOver = dragOverKey === asset.fileKey;
                return (
              <Box
                key={asset.id}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (editable && targetId) setDragOverKey(asset.fileKey);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverKey(null);
                }}
                onDrop={handleDropOnBlock(asset.fileKey)}
                sx={{
                  position: 'relative',
                  border: (theme) =>
                    `1px solid ${isDragOver ? theme.palette.primary.main : theme.palette.divider}`,
                  borderRadius: 1,
                  p: 1,
                  display: 'grid',
                  rowGap: 1,
                  bgcolor: isDragOver ? 'action.hover' : undefined,
                  transition: 'border-color 0.2s, background-color 0.2s',
                }}
              >
                {((uploadingKey === asset.fileKey) || (actionLoading?.key === `block-${asset.id}`)) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(255,255,255,0.85)',
                      borderRadius: 1,
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress size={36} />
                  </Box>
                )}
                <Box
                  sx={{
                    width: '100%',
                    height: 120,
                    borderRadius: 1,
                    bgcolor: 'grey.200',
                    color: 'text.primary',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    fontWeight: 700,
                    letterSpacing: 0.8,
                  }}
                >
                  {String(asset.mimeType || '').startsWith('image/') && privateAssetImages[asset.fileKey] ? (
                    <Box
                      component="img"
                      src={privateAssetImages[asset.fileKey]}
                      alt={asset.fileKey}
                      sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        bgcolor: getFileBadgeStyle(asset.originalName).bg,
                        color: getFileBadgeStyle(asset.originalName).color,
                        fontSize: getFileBadgeStyle(asset.originalName).fontSize,
                        fontWeight: 800,
                        letterSpacing: 1.1,
                        textTransform: 'uppercase',
                      }}
                    >
                      {getFileExtLabel(asset.originalName)}
                    </Box>
                  )}
                </Box>

                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {asset.fileKey}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                  {asset.originalName}
                </Typography>
                {editingCommentId === asset.id ? (
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={t('Comment')}
                    value={editingCommentValue}
                    onChange={(e) => setEditingCommentValue(e.target.value)}
                    onBlur={async () => {
                      try {
                        await updatePrivateFileComment(
                          entityType,
                          targetId,
                          asset.id,
                          editingCommentValue.trim() || null
                        );
                        enqueueSnackbar(t('Comment saved'), { variant: 'success' });
                        await refreshPrivateAssets();
                      } catch (err: any) {
                        enqueueSnackbar(err?.message || t('Failed to save comment'), { variant: 'error' });
                      }
                      setEditingCommentId(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    }}
                    sx={{ '& .MuiInputBase-input': { fontSize: 13 } }}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      minHeight: 24,
                    }}
                  >
                    {asset.comment ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ flex: 1, fontStyle: 'italic', wordBreak: 'break-word' }}
                      >
                        {asset.comment}
                      </Typography>
                    ) : editable ? (
                      <Typography variant="caption" color="text.disabled" sx={{ flex: 1 }}>
                        {t('No comment')}
                      </Typography>
                    ) : null}
                    {editable && (
                      <IconButton
                        size="small"
                        sx={{ p: 0.25 }}
                        onClick={() => {
                          setEditingCommentId(asset.id);
                          setEditingCommentValue(asset.comment || '');
                        }}
                      >
                        <Iconify icon="eva:edit-2-outline" width={16} />
                      </IconButton>
                    )}
                  </Box>
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ minHeight: 18, visibility: isImageAsset ? 'hidden' : 'visible' }}
                >
                  {t('Versions')}: {groupedByKey[asset.fileKey]?.length || 1}
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {editable && (
                    <Button
                      component="label"
                      size="small"
                      variant="outlined"
                      disabled={uploadingKey === asset.fileKey}
                      startIcon={
                        uploadingKey === asset.fileKey ? (
                          <CircularProgress size={14} />
                        ) : (
                          <Iconify icon="eva:cloud-upload-fill" width={16} />
                        )
                      }
                    >
                      {t('Upload')}
                      <input
                        hidden
                        type="file"
                        accept={
                          allowedExtensions?.length
                            ? allowedExtensions.join(',')
                            : allowedMimeTypes?.join(',')
                        }
                        onChange={async (event) => {
                          const file = event.target.files?.[0];
                          event.target.value = '';
                          if (!file) return;
                          await handleUploadToKey(file, asset.fileKey);
                        }}
                      />
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={actionLoading?.key === `block-${asset.id}`}
                    startIcon={
                      actionLoading?.key === `block-${asset.id}` && actionLoading?.action === 'open' ? (
                        <CircularProgress size={14} />
                      ) : (
                        <Iconify icon="eva:eye-fill" width={16} />
                      )
                    }
                    onClick={async () => {
                      setActionLoading({ key: `block-${asset.id}`, action: 'open' });
                      try {
                        const blob = await getPrivateFileBlobById(entityType, targetId, asset.id);
                        const url = URL.createObjectURL(blob);
                        window.open(url, '_blank');
                        setTimeout(() => URL.revokeObjectURL(url), 60_000);
                      } catch (err: any) {
                        enqueueSnackbar(err?.message || t('Failed to open asset'), { variant: 'error' });
                      } finally {
                        setActionLoading(null);
                      }
                    }}
                  >
                    {t('Open')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={actionLoading?.key === `block-${asset.id}`}
                    startIcon={
                      actionLoading?.key === `block-${asset.id}` && actionLoading?.action === 'download' ? (
                        <CircularProgress size={14} />
                      ) : (
                        <Iconify icon="eva:download-fill" width={16} />
                      )
                    }
                    onClick={async () => {
                      setActionLoading({ key: `block-${asset.id}`, action: 'download' });
                      try {
                        const blob = await getPrivateFileBlobById(entityType, targetId, asset.id);
                        downloadBlob(blob, asset.originalName || `${asset.fileKey}.bin`);
                      } catch (err: any) {
                        enqueueSnackbar(err?.message || t('Failed to download asset'), { variant: 'error' });
                      } finally {
                        setActionLoading(null);
                      }
                    }}
                  >
                    {t('Download')}
                  </Button>
                  {!isImageAsset && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setHistoryKey(asset.fileKey)}
                    >
                      {t('History')}
                    </Button>
                  )}
                  {isPdfAsset && editable && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Iconify icon="eva:edit-2-fill" width={16} />}
                      onClick={() => setSignDrawerTarget(asset)}
                    >
                      {t('Sign')}
                    </Button>
                  )}
                  {editable && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        setDeleteTarget({
                          id: asset.id,
                          fileKey: asset.fileKey,
                          originalName: asset.originalName,
                          version: asset.version,
                          source: 'asset-key',
                        });
                      }}
                    >
                      {t('Delete')}
                    </Button>
                  )}
                </Stack>
              </Box>
                );
              })()
            ))
          ) : null}
          {editable && presetKeysWithoutFiles.map((presetKey) => {
            const isDragOver = dragOverKey === presetKey;
            return (
              <Box
                key={`preset-${presetKey}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (editable && targetId) setDragOverKey(presetKey);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverKey(null);
                }}
                onDrop={handleDropOnBlock(presetKey)}
                sx={{
                  position: 'relative',
                  border: (theme) =>
                    `1px dashed ${isDragOver ? theme.palette.primary.main : theme.palette.divider}`,
                  borderRadius: 1,
                  p: 1,
                  display: 'grid',
                  rowGap: 1,
                  bgcolor: isDragOver ? 'action.hover' : undefined,
                  minHeight: 120,
                  transition: 'border-color 0.2s, background-color 0.2s',
                }}
              >
                {uploadingKey === presetKey && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(255,255,255,0.85)',
                      borderRadius: 1,
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress size={36} />
                  </Box>
                )}
                <Box
                  sx={{
                    width: '100%',
                    height: 120,
                    borderRadius: 1,
                    bgcolor: 'grey.100',
                    color: 'grey.700',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                  }}
                >
                  <Iconify icon="eva:file-add-outline" width={32} sx={{ opacity: 0.9 }} />
                  <Typography variant="caption">{t('Drop file or upload')}</Typography>
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {presetKey}
                </Typography>
                {editable && (
                  <Button
                    component="label"
                    size="small"
                    variant="outlined"
                    disabled={!targetId || !!(uploadingKey && uploadingKey === presetKey)}
                    startIcon={
                      uploadingKey === presetKey ? (
                        <CircularProgress size={14} />
                      ) : (
                        <Iconify icon="eva:cloud-upload-fill" width={16} />
                      )
                    }
                  >
                    {t('Upload')}
                    <input
                      hidden
                      type="file"
                      accept={
                        allowedExtensions?.length
                          ? allowedExtensions.join(',')
                          : allowedMimeTypes?.join(',')
                      }
                      onChange={async (event) => {
                        const file = event.target.files?.[0];
                        event.target.value = '';
                        if (!file) return;
                        await handleUploadToKey(file, presetKey);
                      }}
                    />
                  </Button>
                )}
              </Box>
            );
          })}
          {latestAssets.length === 0 && presetKeysWithoutFiles.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              {t('No private assets uploaded')}
            </Typography>
          )}
        </Box>
      </Stack>
      <Drawer
        anchor="right"
        open={!!historyKey}
        onClose={() => {
          setHistoryKey(null);
          setEditingCommentId(null);
        }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, p: 2 } }}
      >
        <Stack spacing={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{t('Asset history')}: {historyKey}</Typography>
            <IconButton onClick={() => setHistoryKey(null)}>
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Box>

          {historyFiles.length > 0 ? (
            historyFiles.map((file) => (
              <Box
                key={file.id}
                sx={{
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  p: 1,
                }}
              >
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2">
                    {file.originalName} {file.version ? `(v${file.version})` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(file.createdAt).toLocaleString()}
                  </Typography>
                  {editingCommentId === file.id ? (
                    <TextField
                      size="small"
                      fullWidth
                      placeholder={t('Comment')}
                      value={editingCommentValue}
                      onChange={(e) => setEditingCommentValue(e.target.value)}
                      onBlur={async () => {
                        try {
                          await updatePrivateFileComment(
                            entityType,
                            targetId,
                            file.id,
                            editingCommentValue.trim() || null
                          );
                          enqueueSnackbar(t('Comment saved'), { variant: 'success' });
                          await refreshPrivateAssets();
                        } catch (err: any) {
                          enqueueSnackbar(err?.message || t('Failed to save comment'), { variant: 'error' });
                        }
                        setEditingCommentId(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                      }}
                      sx={{ '& .MuiInputBase-input': { fontSize: 13 } }}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        minHeight: 24,
                      }}
                    >
                      {file.comment ? (
                        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                          {file.comment}
                        </Typography>
                      ) : editable ? (
                        <Typography variant="caption" color="text.disabled" sx={{ flex: 1 }}>
                          {t('No comment')}
                        </Typography>
                      ) : null}
                      {editable && (
                        <IconButton
                          size="small"
                          sx={{ p: 0.25 }}
                          onClick={() => {
                            setEditingCommentId(file.id);
                            setEditingCommentValue(file.comment || '');
                          }}
                        >
                          <Iconify icon="eva:edit-2-outline" width={16} />
                        </IconButton>
                      )}
                    </Box>
                  )}
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      size="small"
                      disabled={actionLoading?.key === `history-${file.id}`}
                      onClick={async () => {
                        setActionLoading({ key: `history-${file.id}`, action: 'open' });
                        try {
                          const blob = await getPrivateFileBlobById(entityType, targetId, file.id);
                          const url = URL.createObjectURL(blob);
                          window.open(url, '_blank');
                          setTimeout(() => URL.revokeObjectURL(url), 60_000);
                        } catch (err: any) {
                          enqueueSnackbar(err?.message || t('Failed to open asset'), { variant: 'error' });
                        } finally {
                          setActionLoading(null);
                        }
                      }}
                    >
                      {actionLoading?.key === `history-${file.id}` && actionLoading?.action === 'open' ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Iconify icon="eva:eye-fill" width={18} />
                      )}
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={actionLoading?.key === `history-${file.id}`}
                      onClick={async () => {
                        setActionLoading({ key: `history-${file.id}`, action: 'download' });
                        try {
                          const blob = await getPrivateFileBlobById(entityType, targetId, file.id);
                          downloadBlob(blob, file.originalName || `${file.fileKey}.bin`);
                        } catch (err: any) {
                          enqueueSnackbar(err?.message || t('Failed to download asset'), { variant: 'error' });
                        } finally {
                          setActionLoading(null);
                        }
                      }}
                    >
                      {actionLoading?.key === `history-${file.id}` && actionLoading?.action === 'download' ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Iconify icon="eva:download-fill" width={18} />
                      )}
                    </IconButton>
                    {editable && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setDeleteTarget({
                            id: file.id,
                            fileKey: file.fileKey,
                            originalName: file.originalName,
                            version: file.version,
                            source: 'history',
                          });
                        }}
                      >
                        <Iconify icon="eva:trash-2-outline" width={18} />
                      </IconButton>
                    )}
                  </Stack>
                </Stack>
              </Box>
            ))
          ) : (
            <Typography variant="caption" color="text.secondary">
              {t('History is empty')}
            </Typography>
          )}
        </Stack>
      </Drawer>
      <Drawer
        anchor="right"
        open={isManageDrawerOpen && isAdmin}
        onClose={() => setIsManageDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 620 }, p: 2 } }}
      >
        <RHFormProvider {...manageFormMethods}>
        <Stack spacing={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{t('Generator buttons editor')}</Typography>
            <IconButton onClick={() => setIsManageDrawerOpen(false)}>
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Box>

          <Typography variant="caption" color="text.secondary">
            {t('Select template, copy prompt to AI, paste resulting JSON, then submit.')}
          </Typography>

          <RHFAuto
            name="selectedTemplate"
            label={t('Document template')}
            optionsLabel="name"
            useHook={useDocumentTemplates as any}
            fields={['assetCode']}
            filters={{}}
            onChoose={(value: any) => {
              handleTemplateChooseForPrompt(value).catch(() => {
                enqueueSnackbar(t('Failed to build prompt from template'), { variant: 'error' });
              });
            }}
          />

          <TextField
            size="small"
            multiline
            minRows={8}
            maxRows={16}
            fullWidth
            label={t('Prompt')}
            value={generatedPrompt}
            InputProps={{ readOnly: true }}
          />
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Tooltip title={t('Copy prompt')}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!generatedPrompt}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(generatedPrompt);
                      enqueueSnackbar(t('Prompt copied'), { variant: 'success' });
                    } catch {
                      enqueueSnackbar(t('Failed to copy prompt'), { variant: 'error' });
                    }
                  }}
                >
                  {t('Copy prompt')}
                </Button>
              </span>
            </Tooltip>
          </Box>

          <RHFJsonEditor
            name="resultJson"
            label={t('Result JSON')}
            rows={12}
            placeholder='{"label":"...","assetCode":"...","values":{},"previewLoops":{},"additionalValues":[]}'
          />
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              disabled={isSavingConfig || !String(manageFormMethods.watch('resultJson') || '').trim()}
              onClick={handleCreateGenerateConfig}
            >
              {t('Submit')}
            </Button>
          </Box>
          {(() => {
            try {
              const parsed = JSON.parse(String(manageFormMethods.watch('resultJson') || '{}'));
              const assetCode = String(parsed?.assetCode || '').trim();
              if (!assetCode) return null;
              const existing = storedGenerateDocuments.find((x) => String(x.assetCode || '') === assetCode);
              if (!existing) return null;
              return (
                <Alert severity="info">
                  {t('Submit will update existing button for assetCode {assetCode}', { assetCode })}
                </Alert>
              );
            } catch {
              return null;
            }
          })()}

          <Typography variant="subtitle2">{t('Buttons for this entity')}</Typography>
          <Stack spacing={1}>
            {storedGenerateDocuments.length > 0 ? (
              storedGenerateDocuments.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                    <Box>
                      <Typography variant="subtitle2">{item.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.assetCode}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Tooltip title={t('Edit')}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditGenerateConfig(item)}
                        >
                          <Iconify icon="eva:edit-2-outline" width={18} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('Delete')}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={isDeletingConfigId === item.id}
                            onClick={() => handleDeleteGenerateConfig(item.id)}
                          >
                            <Iconify icon="eva:trash-2-outline" width={18} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              ))
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t('No generator buttons configured')}
              </Typography>
            )}
          </Stack>
        </Stack>
        </RHFormProvider>
      </Drawer>
      <Dialog
        open={!!generateConfirmTarget}
        onClose={() => {
          if (isGenerating) return;
          setGenerateConfirmTarget(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t('Create document {name}?', { name: generateConfirmTarget?.label || '' })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('A new file will be generated from template asset code {assetCode}.', {
              assetCode: generateConfirmTarget?.assetCode || '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={() => setGenerateConfirmTarget(null)}
            disabled={isGenerating}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!generateConfirmTarget) return;
              await runGenerateDocument(generateConfirmTarget, {});
              setGenerateConfirmTarget(null);
            }}
            disabled={isGenerating}
          >
            {t('Create')}
          </Button>
        </DialogActions>
      </Dialog>
      {generateDrawerTarget && (
        <GenerateDrawerForm
          config={generateDrawerTarget}
          initialValues={generateAdditionalValues}
          generateContext={generateContext}
          isGenerating={isGenerating}
          onCancel={() => setGenerateDrawerTarget(null)}
          onSubmit={async (values) => {
            await runGenerateDocument(generateDrawerTarget, values);
            setGenerateDrawerTarget(null);
          }}
          onValidationError={(fieldLabel) =>
            enqueueSnackbar(t('Field {name} is required', { name: fieldLabel }), { variant: 'error' })
          }
        />
      )}
      {signDrawerTarget && targetId && (
        <SignPdfDrawer
          open={!!signDrawerTarget}
          onClose={() => setSignDrawerTarget(null)}
          onSuccess={() => refreshPrivateAssets()}
          entityType={entityType}
          entityId={targetId}
          asset={signDrawerTarget}
        />
      )}
      <Dialog
        open={!!deleteTarget}
        onClose={() => {
          if (isDeleting) return;
          setDeleteTarget(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {deleteTarget?.source === 'asset-key'
            ? t('Delete asset key: {key}', { key: deleteTarget?.fileKey || '' })
            : t('Delete file version')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget?.source === 'asset-key' ? (
              <>
                {t('Are you sure you want to delete file for asset key {key}?', {
                  key: deleteTarget?.fileKey || '',
                })}{' '}
                {deleteTarget?.originalName || ''}
              </>
            ) : (
              <>
                {t('Are you sure you want to delete this file version?')}{' '}
                {deleteTarget?.originalName || ''}{' '}
                {deleteTarget?.version ? `(v${deleteTarget.version})` : ''}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={() => setDeleteTarget(null)}
            disabled={isDeleting}
          >
            {t('Cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
          >
            {t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </CardBlock>
  );
}
